import { useState, useCallback } from 'react';
import { api, API_BASE_URL } from '../../config';

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
  currentChunk: number;
  totalChunks: number;
}

interface ChunkedUploadOptions {
  onProgress?: (progress: UploadProgress) => void;
}

export function useChunkedUpload(options?: ChunkedUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const calculateSHA512 = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-512', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const upload = useCallback(async (
    file: File,
    metadata: {
      version: string;
      channel: string;
      platform: string;
      architecture?: string;
      releaseNotes?: string;
      minimumAppVersion?: string;
      isMandatory?: boolean;
    }
  ) => {
    setIsUploading(true);
    setError(null);

    try {
      // Calculate SHA512
      const sha512 = await calculateSHA512(file);

      // Prepare upload
      const prepareResponse = await api.post<{
        uploadId: string;
        fileKey: string;
        chunkSize: number;
        totalChunks: number;
      }>('/api/v1/binaries/upload/prepare', {
        fileName: file.name,
        fileSize: file.size,
        version: metadata.version,
        channel: metadata.channel,
        platform: metadata.platform,
        architecture: metadata.architecture || 'x64',
      });

      const { uploadId, fileKey, chunkSize, totalChunks } = prepareResponse;

      // Configure parallel upload settings
      const PARALLEL_UPLOADS = 6; // Number of chunks to upload in parallel (optimize for speed)
      const uploadedChunks = new Set<number>();
      let failedChunks: number[] = [];
      
      // Track progress across all chunks
      const chunkProgress = new Map<number, number>();
      
      const updateOverallProgress = () => {
        const totalProgress = Array.from(chunkProgress.values()).reduce((sum, progress) => sum + progress, 0);
        const overallPercentage = Math.round((totalProgress / file.size) * 100);
        
        if (options?.onProgress) {
          options.onProgress({
            loaded: totalProgress,
            total: file.size,
            percentage: overallPercentage,
            currentChunk: uploadedChunks.size,
            totalChunks,
          });
        }
      };

      // Upload a single chunk
      const uploadChunk = async (chunkIndex: number): Promise<void> => {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        const chunkSizeBytes = end - start;

        try {
          // eslint-disable-next-line no-restricted-syntax -- ApiClient always JSON.stringify()s the body and sets Content-Type: application/json; this PUT sends a raw binary Blob chunk and must not be coerced through that path
          const response = await fetch(`${API_BASE_URL}/api/v1/binaries/upload/chunk/${uploadId}/${chunkIndex}`, {
            method: 'PUT',
            credentials: 'include',
            body: chunk,
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          uploadedChunks.add(chunkIndex);
          chunkProgress.set(chunkIndex, chunkSizeBytes);
          updateOverallProgress();
        } catch (error) {
          console.error(`Failed to upload chunk ${chunkIndex}:`, error);
          failedChunks.push(chunkIndex);
          throw new Error(`Failed to upload chunk ${chunkIndex + 1}/${totalChunks}`);
        }
      };

      // Upload chunks in parallel batches
      for (let i = 0; i < totalChunks; i += PARALLEL_UPLOADS) {
        const batch = [];
        for (let j = 0; j < PARALLEL_UPLOADS && i + j < totalChunks; j++) {
          batch.push(uploadChunk(i + j));
        }
        
        // Wait for the current batch to complete before starting the next
        await Promise.all(batch);
      }

      // Retry failed chunks if any
      if (failedChunks.length > 0) {
        console.log(`Retrying ${failedChunks.length} failed chunks...`);
        for (const chunkIndex of failedChunks) {
          await uploadChunk(chunkIndex);
        }
        failedChunks = [];
      }

      // Complete upload
      const completeResponse = await api.post<{
        success: boolean;
        release?: any;
        fileUrl: string;
        fileSize: number;
        sha512: string;
      }>('/api/v1/binaries/upload/complete', {
        uploadId,
        fileKey,
        totalChunks,
        sha512,
        version: metadata.version,
        channel: metadata.channel,
        platform: metadata.platform,
        architecture: metadata.architecture || 'x64',
        releaseNotes: metadata.releaseNotes,
        minimumAppVersion: metadata.minimumAppVersion,
        isMandatory: metadata.isMandatory,
      });

      setIsUploading(false);
      return completeResponse;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Upload failed');
      setError(error);
      setIsUploading(false);
      throw error;
    }
  }, [options]);

  return {
    upload,
    isUploading,
    error,
  };
}